import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { Sakura } from "../components/shrine/Sakura";
import {
  TIERS,
  getMaxPairTotal,
  getNextTier,
  getPairs,
  type StoredPair,
} from "../lib/storage";
import {
  MIKO_CHARACTERS,
  isMikoUnlocked,
  type MikoCharacter,
} from "../lib/miko";
import { useSound } from "../hooks/useSound";
import { useVibration } from "../hooks/useVibration";

/**
 * §2.7 Progress
 *
 * - 各ペアごとに 5/10/30/50/100/365 のティアゲージ
 * - 次のアンロックまでの残り回数
 * - アンロック済み巫女キャラ + 未アンロックはシルエット
 * - シルエットを長押しすると「あと何回で会える」がふわっと出る (光の演出)
 */

export function Progress() {
  const { t, i18n } = useTranslation();
  const [pairs, setPairs] = useState<StoredPair[]>([]);
  const [maxTotal, setMaxTotal] = useState(0);

  useEffect(() => {
    setPairs(getPairs());
    setMaxTotal(getMaxPairTotal());
  }, []);

  const sortedPairs = useMemo(
    () => [...pairs].sort((a, b) => b.totalDraws - a.totalDraws),
    [pairs],
  );

  return (
    <main className="relative min-h-dvh bg-[var(--color-night-deep)] text-[var(--color-washi)] overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center 80%, rgba(159,29,53,0.18), transparent 60%), linear-gradient(to bottom, #050203 0%, #0a0608 60%, #1a0c12 100%)",
        }}
      />
      <Sakura count={6} />

      <header className="relative z-10 px-6 pt-10 pb-4">
        <Link
          href="/home"
          className="text-[10px] tracking-[0.3em] text-[var(--color-gold-light)] opacity-70"
        >
          ← {t("common.back")}
        </Link>
        <h1
          className="mt-4 text-2xl tracking-[0.3em] text-[var(--color-gold-light)]"
          style={{
            fontFamily: "var(--font-fude)",
            textShadow: "0 0 18px rgba(201,169,97,0.4)",
          }}
        >
          {t("progress.title")}
        </h1>
      </header>

      <section className="relative z-10 px-6 pb-12">
        {sortedPairs.length === 0 ? (
          <p className="opacity-60 text-sm mt-6">{t("progress.noPairs")}</p>
        ) : (
          <ul className="flex flex-col gap-6 mt-2">
            {sortedPairs.map((p) => (
              <li key={p.pairId}>
                <PairTierCard pair={p} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="relative z-10 px-6 pb-32">
        <h2
          className="text-base tracking-[0.3em] text-[var(--color-gold-light)] opacity-90"
          style={{ fontFamily: "var(--font-fude)" }}
        >
          ✦ {t("progress.characters")}
        </h2>
        <ul className="mt-4 grid grid-cols-3 gap-3">
          {MIKO_CHARACTERS.map((m) => (
            <li key={m.id}>
              <MikoTile
                miko={m}
                unlocked={isMikoUnlocked(m, maxTotal)}
                maxTotal={maxTotal}
                locale={i18n.language === "en" ? "en" : "ja"}
              />
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}

function PairTierCard({ pair }: { pair: StoredPair }) {
  const { t } = useTranslation();
  const next = getNextTier(pair);
  const tiers = TIERS;
  const currentMax = next ? next.tier : tiers[tiers.length - 1];
  const ratio = Math.min(1, pair.totalDraws / currentMax);

  return (
    <article
      className="relative rounded-2xl p-5 overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, rgba(40,18,24,0.7) 0%, rgba(10,6,8,0.85) 100%)",
        border: "1px solid rgba(201,169,97,0.25)",
        boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
      }}
    >
      <div className="flex items-end justify-between gap-3">
        <h3
          className="text-xl tracking-[0.25em] text-[var(--color-washi-warm)]"
          style={{ fontFamily: "var(--font-fude)" }}
        >
          {pair.partnerName}
        </h3>
        <p className="text-[10px] tracking-widest text-[var(--color-gold-light)] opacity-80">
          {t("pair.totalDraws", { n: pair.totalDraws })}
        </p>
      </div>

      {/* ゲージ */}
      <div className="mt-4 relative h-2 rounded-full bg-white/8 overflow-hidden">
        <motion.div
          className="h-full"
          style={{
            background: "linear-gradient(90deg, #c43a55, #c9a961, #fffacd)",
            boxShadow: "0 0 12px rgba(201,169,97,0.55)",
          }}
          initial={{ width: 0 }}
          animate={{ width: `${ratio * 100}%` }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
        />
      </div>

      {/* ティアマーカー */}
      <div className="relative mt-2 h-3">
        {tiers.map((tier) => {
          const left = `${(Math.min(tier, currentMax) / currentMax) * 100}%`;
          const reached = pair.totalDraws >= tier;
          if (tier > currentMax) return null;
          return (
            <span
              key={tier}
              className="absolute -translate-x-1/2 text-[8px] tracking-widest"
              style={{
                left,
                color: reached
                  ? "var(--color-gold-light)"
                  : "rgba(201,169,97,0.35)",
                fontFamily: "var(--font-fude)",
              }}
            >
              {tier}
            </span>
          );
        })}
      </div>

      {/* 残り */}
      {next ? (
        <p
          className="mt-3 text-[11px] tracking-[0.2em] text-[var(--color-washi)] opacity-90"
          style={{ fontFamily: "var(--font-fude)" }}
        >
          {t("progress.tiers", { n: next.tier, m: next.remaining })}
        </p>
      ) : (
        <p
          className="mt-3 text-[11px] tracking-[0.2em] text-[var(--color-gold-light)]"
          style={{ fontFamily: "var(--font-fude)" }}
        >
          ✦ 365 ✦
        </p>
      )}

      {/* 既に達成したティアの装飾 */}
      <div className="mt-4 flex flex-wrap gap-1.5">
        {tiers.map((tier) => {
          const reached = pair.totalDraws >= tier;
          return (
            <span
              key={tier}
              title={String(tier)}
              className="px-2 py-0.5 rounded-full text-[9px] tracking-widest transition"
              style={{
                fontFamily: "var(--font-fude)",
                color: reached ? "#1a1a1a" : "var(--color-gold-light)",
                background: reached
                  ? "linear-gradient(135deg, #c9a961, #fffacd)"
                  : "rgba(201,169,97,0.08)",
                border: reached
                  ? "1px solid rgba(255,250,205,0.7)"
                  : "1px solid rgba(201,169,97,0.2)",
                boxShadow: reached
                  ? "0 0 12px rgba(201,169,97,0.6)"
                  : undefined,
              }}
            >
              {tier}
            </span>
          );
        })}
      </div>

      {/* 連続日数 */}
      {pair.currentStreak > 0 && (
        <p className="mt-3 text-[10px] tracking-widest text-[var(--color-shu-light)] opacity-90">
          {t("pair.currentStreak", { n: pair.currentStreak })}
        </p>
      )}
    </article>
  );
}

function MikoTile({
  miko,
  unlocked,
  maxTotal,
  locale,
}: {
  miko: MikoCharacter;
  unlocked: boolean;
  maxTotal: number;
  locale: "ja" | "en";
}) {
  const { t } = useTranslation();
  const { play, unlock } = useSound();
  const { vibrate } = useVibration();
  const remaining = Math.max(0, miko.unlockTier - maxTotal);

  return (
    <motion.button
      whileTap={{ scale: 0.94 }}
      whileHover={{ y: -3 }}
      onClick={() => {
        unlock();
        if (unlocked) {
          play("mikoVoice");
          vibrate(20);
        } else {
          play("woodTap");
        }
      }}
      className="relative w-full aspect-[3/4] rounded-xl overflow-hidden flex flex-col items-center justify-end pb-3 px-2"
      style={{
        background: unlocked
          ? `linear-gradient(180deg, rgba(40,18,24,0.4), ${miko.accent}22 60%, rgba(10,6,8,0.9) 100%)`
          : "linear-gradient(180deg, rgba(10,6,8,0.95), rgba(20,12,16,0.95))",
        border: unlocked
          ? `1px solid ${miko.accent}88`
          : "1px solid rgba(201,169,97,0.15)",
        boxShadow: unlocked
          ? `0 0 24px ${miko.accent}55, inset 0 0 24px ${miko.accent}22`
          : "inset 0 0 32px rgba(0,0,0,0.6)",
      }}
    >
      {/* 巫女の glyph (placeholder — Track C 立ち絵で差し替え) */}
      <div className="flex-1 flex items-center justify-center w-full pt-2">
        <motion.span
          className="text-6xl"
          style={{
            fontFamily: "var(--font-fude)",
            color: unlocked ? miko.accent : "rgba(201,169,97,0.18)",
            filter: unlocked
              ? `drop-shadow(0 0 12px ${miko.accent}cc)`
              : "blur(2px)",
          }}
          animate={
            unlocked
              ? { scale: [1, 1.04, 1], opacity: [0.85, 1, 0.85] }
              : undefined
          }
          transition={
            unlocked
              ? { duration: 4, repeat: Infinity, ease: "easeInOut" }
              : undefined
          }
          aria-hidden
        >
          {miko.glyph}
        </motion.span>
      </div>

      {/* ラベル */}
      <span
        className="text-[11px] tracking-[0.2em]"
        style={{
          fontFamily: "var(--font-fude)",
          color: unlocked
            ? "var(--color-washi-warm)"
            : "var(--color-gold-light)",
          opacity: unlocked ? 1 : 0.5,
        }}
      >
        {unlocked ? (locale === "en" ? miko.name.en : miko.name.ja) : "???"}
      </span>

      <span
        className="text-[8px] tracking-widest mt-0.5"
        style={{
          color: unlocked
            ? "var(--color-gold-light)"
            : "var(--color-shu-light)",
          opacity: 0.8,
        }}
      >
        {unlocked
          ? t("progress.unlocked")
          : remaining > 0
            ? `${remaining} more`
            : t("progress.unlocked")}
      </span>
    </motion.button>
  );
}
